






























































































































































































import { Component, Prop, Vue } from 'vue-property-decorator';
import Checkbox from '@/components/Form/Checkbox.vue';
import Alert from '@/components/Global/Alert.vue';
import { DownloadButton } from '@/components/Global/Downloads/DownloadButton.vue';
import { DownloadIcon } from '@/components/Global/Downloads/DownloadIcon.vue';
import { DownloadProgress } from '@/components/Global/Downloads/DownloadProgress.vue';
import { DocumentStatusMessageType } from '@/services/AsyncNotifications';
import {
	Download,
	DownloadQueue,
	Ready,
	resolveDownloadUrl,
	UrlReadyWhen,
} from '@/services/Downloads';
import PolicyDocuments, { Metadata } from '@/services/PolicyDocuments';
import Reports from '@/services/Reports';
import { capitalize, wrapIfExceeded as wrapIfExceededUtil } from '@/utils/Utils';
import { formatTime } from '@/utils/DateTime';
import { DocumentSet, DocumentSets } from './DocumentSets';

type DocumentComparer = (left: PolicyDocument, right: PolicyDocument) => number;

const documentDateComparer = (dateField: keyof Metadata): DocumentComparer => (
	left: PolicyDocument,
	right: PolicyDocument,
): number =>
	new Date(left.metadata[dateField]).getTime() - new Date(right.metadata[dateField]).getTime();

const documentStringComparer = (stringField: keyof Metadata): DocumentComparer => (
	left: PolicyDocument,
	right: PolicyDocument,
): number => left.metadata[stringField].localeCompare(right.metadata[stringField]);

type TableColumn = keyof Pick<
	Metadata,
	'DocumentDate' | 'description' | 'docName' | 'effectiveDate' | 'endorsementType' | 'riskName'
>;

interface TableColumnSortConfig {
	comparer: DocumentComparer;
	descending: boolean;
}

type TableSortConfig = { [P in TableColumn]: TableColumnSortConfig };

interface PolicyDocument {
	downloadId: string;
	metadata: Metadata;
}

@Component({
	name: 'PolicyDocumentsTable',
	components: {
		Alert,
		DownloadButton,
		DownloadIcon,
		DownloadProgress,
	},
})
export default class PolicyDocumentsTable extends Vue {
	@Prop() private documentSet: DocumentSet;
	@Prop() private metadatas: Metadata[];

	private DocumentSets = DocumentSets;

	private downloadAllId: string = '';
	private filteredDocuments: PolicyDocument[] = [];
	private readonly paginate: string[] = ['sortedDocuments'];

	private get sortedDocuments(): PolicyDocument[] {
		const columnSortConfig: TableColumnSortConfig | undefined = this.sortConfig[
			this.sortColumn
		];
		return this.filteredDocuments
			.slice()
			.sort((left: PolicyDocument, right: PolicyDocument): number => {
				const compareResult: number = columnSortConfig.comparer(left, right);
				return columnSortConfig.descending ? -compareResult : compareResult;
			});
	}

	private readonly sortConfig: TableSortConfig = {
		DocumentDate: {
			comparer: documentDateComparer('DocumentDate'),
			descending: true,
		},
		description: {
			comparer: documentStringComparer('description'),
			descending: false,
		},
		docName: {
			comparer: documentStringComparer('docName'),
			descending: false,
		},
		effectiveDate: {
			comparer: documentDateComparer('effectiveDate'),
			descending: false,
		},
		endorsementType: {
			comparer: documentStringComparer('endorsementType'),
			descending: false,
		},
		riskName: {
			comparer: documentStringComparer('riskName'),
			descending: false,
		},
	};
	private sortColumn: TableColumn = 'DocumentDate';

	private static readonly _zippedDocumentSets: readonly DocumentSet[] = Object.freeze([
		DocumentSets.cois,
		DocumentSets.endorsements,
		// TODO: Downloading a State Fund Forms packet/bundle doesn't work at the time of this
		// writing. Once it is working, confirm that it will be a zipped bundle instead of a PDF
		// packet.
		DocumentSets.stateFundForms,
	]);

	private created(): void {
		const generateDownloadId = (documentId: string): string =>
			[
				this.$route.path,
				this.metadatas[0].policyNum,
				...this.documentSet.docTypes,
				documentId,
			].join(':');

		this.downloadAllId = generateDownloadId('all');
		this.filteredDocuments = this.metadatas
			.filter(metadata => this.documentSet.docTypes.includes(metadata.docType))
			.map(
				(metadata: Metadata): PolicyDocument => ({
					downloadId: generateDownloadId(metadata.docId),
					metadata,
				}),
			);
	}

	private sortTable(column: TableColumn) {
		if (column !== this.sortColumn) {
			this.sortColumn = column;
		} else {
			this.sortConfig[column].descending = !this.sortConfig[column].descending;
		}
	}

	private getSortIndicatorClasses(column: TableColumn): string {
		return column === this.sortColumn
			? `fas ${this.sortConfig[column].descending ? 'fa-sort-down' : 'fa-sort-up'}`
			: 'd-none';
	}

	private get bundleOrPacket(): string {
		return PolicyDocumentsTable._zippedDocumentSets.includes(this.documentSet)
			? 'Bundle'
			: 'Packet';
	}

	private titleCase(text: string | undefined): string {
		return capitalize((text ?? '').toLowerCase());
	}

	private async resolveDocumentUrl(params: {
		id: string;
		policyNumber: string;
		year: string;
	}): Promise<string> {
		const response = await PolicyDocuments.requestPolicyDocument(
			params.policyNumber,
			params.id,
			params.year,
		);
		const urlReadyWhen: UrlReadyWhen = {
			ready: response.fetching ? Ready.later : Ready.now,
			url: response.s3url,
		};
		return resolveDownloadUrl(
			urlReadyWhen,
			DocumentStatusMessageType.policyDocument,
			params.id,
		);
	}

	private downloadDocument(document: PolicyDocument): Promise<Download> {
		const title: string =
			this.documentSet === DocumentSets.endorsements
				? `${this.titleCase(document.metadata.description)} ${this.titleCase(
						document.metadata.endorsementType,
				  )}`
				: this.titleCase(document.metadata.docName || this.documentSet.long.singular);

		return DownloadQueue.singleton.enqueue({
			fileNameFromClient: Reports.generateFileName({
				date: document.metadata.year,
				extensionWithoutSeparator: 'pdf',
				policyNumber: document.metadata.policyNum,
				title,
			}),
			id: document.downloadId,
			label: document.metadata.docName,
			urlPromise: this.resolveDocumentUrl({
				id: document.metadata.docId,
				policyNumber: document.metadata.policyNum,
				year: document.metadata.year,
			}),
		});
	}

	private downloadAllDocuments(): Array<Promise<Download>> {
		const extensionWithoutSeparator: string = PolicyDocumentsTable._zippedDocumentSets.includes(
			this.documentSet,
		)
			? 'zip'
			: 'pdf';

		return this.documentSet.docTypes.map(
			(docType: string): Promise<Download> =>
				DownloadQueue.singleton.enqueue({
					fileNameFromClient: Reports.generateFileName({
						date: this.metadatas[0].year,
						extensionWithoutSeparator,
						policyNumber: this.metadatas[0].policyNum,
						subtitle: this.bundleOrPacket,
						title: this.documentSet.long.plural,
					}),
					id: this.downloadAllId,
					label: this.documentSet.long.singular,
					urlPromise: this.resolveDocumentUrl({
						id: docType,
						policyNumber: this.metadatas[0].policyNum,
						year: this.metadatas[0].year,
					}),
				}),
		);
	}
}
